import React, { FC, useState, useEffect, useRef, useMemo } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { Message, Conversation, Tag, ConfigOption } from '../hooks/useChatGraphQL'
import { Button, Form, Modal } from 'react-bootstrap'
import { toast } from 'react-toastify'
import TagManager from './TagManager'
import { URL as BACKEND_URL } from '../../../gql/client'
import { STORAGE_NAMES } from '../../../helpers/consts'
import { useGetUser } from '../../../store/userStore'
import { isAdministrator } from '../../../helpers'
import { PhoneProviderActions } from '../../../components/telephony/PhoneProviderActions'
import { GET_MESSAGE_TEMPLATES } from '../../../gql/queries/campaigns'
import { CREATE_MESSAGE_TEMPLATE, TOGGLE_TEMPLATE_BLUEPRINT, UPDATE_MESSAGE_TEMPLATE } from '../../../gql/mutations/campaigns'
import { IMessageTemplate, IMessageTemplatesRes } from '../../../types/campaigns'
import { getChannelBadgeInfo, getChannelBadgeTooltip } from '../helpers/channelBadgeHelper'

function formatDateSeparator(date: Date): string {
    const now = new Date()
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const startOfYesterday = new Date(startOfToday.getTime() - 86400000)
    const startOfWeekAgo = new Date(startOfToday.getTime() - 6 * 86400000)

    if (date >= startOfToday) return 'Today'
    if (date >= startOfYesterday) return 'Yesterday'
    if (date >= startOfWeekAgo)
        return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
    return date.toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' })
}

type QuickReplyScope = 'USER' | 'ORGANIZATION'

type QuickReply = {
    id: string
    title: string
    content: string
    scope: QuickReplyScope
    createdByUserId: string | null
    organizationId: string | null
    updatedAt: string
}

const getConversationNameParts = (fullName?: string | null) => {
    const safeName = (fullName || '').trim()
    const [firstName = '', ...rest] = safeName.split(/\s+/).filter(Boolean)

    return {
        firstName,
        lastName: rest.join(' '),
        fullName: safeName,
    }
}

const parseTemplateMetadata = (metadata: unknown): Record<string, any> => {
    if (!metadata) return {}
    try {
        if (typeof metadata === 'string') {
            return JSON.parse(metadata)
        }
        if (typeof metadata === 'object') {
            return metadata as Record<string, any>
        }
        return {}
    } catch (error) {
        console.warn('Unable to parse template metadata', error)
        return {}
    }
}

const toQuickReply = (template: IMessageTemplate): QuickReply | null => {
    const metadata = parseTemplateMetadata(template.metadata)
    if (!metadata.quick_reply) return null
    if (template.isActive === false) return null

    return {
        id: template.id,
        title: template.name,
        content: template.body || '',
        scope: template.isBlueprint ? 'ORGANIZATION' : 'USER',
        createdByUserId: metadata.quick_reply_owner_user_id || null,
        organizationId: metadata.quick_reply_organization_id || null,
        updatedAt: template.updatedAt || metadata.updatedAt || new Date().toISOString(),
    }
}

const personalizeQuickReply = (
    content: string,
    conversation: Conversation | null,
    currentUserName?: string | null,
    organizationName?: string | null,
    agentWhatsAppNumber?: string | null
) => {
    const { firstName, lastName, fullName } = getConversationNameParts(conversation?.full_name)

    // Build wa.me link from the agent's WhatsApp number (digits only)
    const waDigits = (agentWhatsAppNumber || '').replace(/\D/g, '')
    const waLink = waDigits ? `https://wa.me/${waDigits}` : ''

    const replacements: Record<string, string> = {
        firstname: firstName,
        first_name: firstName,
        fistname: firstName,
        lastname: lastName,
        last_name: lastName,
        fullname: fullName,
        full_name: fullName,
        phone: conversation?.phone || '',
        agentname: currentUserName || '',
        agent_name: currentUserName || '',
        organization: organizationName || '',
        organization_name: organizationName || '',
        minumerowhatsapp: waLink,
        mi_numero_whatsapp: waLink,
        minumero: waLink,
    }

    return content.replace(/\{([^}]+)\}/g, (match, rawKey) => {
        const normalizedKey = String(rawKey).trim().toLowerCase()
        const replacement = replacements[normalizedKey]
        return replacement !== undefined && replacement !== '' ? replacement : match
    })
}

type Props = {
    activeConversation: Conversation | null
    messages: Message[]
    loading: boolean
    onSendMessage: (contactId: number, content: string, channel: 'WHATSAPP' | 'SMS', configId?: number) => void
    onSendMessageWithMedia?: (contactId: number, content: string, channel: 'WHATSAPP' | 'SMS', file: File, configId?: number) => void
    onOpenTemplateDrawer?: (contactId: number) => void

    // Tag Props
    availableTags: Tag[]
    onCreateTag: (name: string, color: string) => Promise<any>
    onManageTag: (contactId: number, tagId: number, action: 'add' | 'remove') => Promise<any>

    // Channel Config
    configSource?: string
    availableConfigs?: ConfigOption[]
}

const ChatWindow: FC<Props> = ({
    activeConversation,
    messages,
    loading,
    onSendMessage,
    onSendMessageWithMedia,
    onOpenTemplateDrawer,
    availableTags,
    onCreateTag,
    onManageTag,
    configSource,
    availableConfigs = []
}) => {
    const [inputText, setInputText] = useState('')
    const [selectedChannel, setSelectedChannel] = useState<'WHATSAPP' | 'SMS'>('SMS')
    const [selectedConfigId, setSelectedConfigId] = useState<number | undefined>(undefined)
    const [showQuickRepliesModal, setShowQuickRepliesModal] = useState(false)
    const [quickReplyTitle, setQuickReplyTitle] = useState('')
    const [quickReplyContent, setQuickReplyContent] = useState('')
    const [quickReplyScope, setQuickReplyScope] = useState<QuickReplyScope>('USER')
    const [editingQuickReplyId, setEditingQuickReplyId] = useState<string | null>(null)
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null)
    const [isDragging, setIsDragging] = useState(false)
    const authUser = useGetUser((state) => state.user)
    const currentUser = authUser?.user
    const isAdmin = isAdministrator(authUser?.permissions || [])
    const currentUserId = currentUser?.id || null
    const currentOrganizationId = currentUser?.organization?.id || null
    const currentOrganizationName = currentUser?.organization?.name || ''
    const currentUserName = [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' ')
    const {
        data: quickRepliesData,
        loading: quickRepliesLoading,
        refetch: refetchQuickReplies,
    } = useQuery<IMessageTemplatesRes>(GET_MESSAGE_TEMPLATES, {
        fetchPolicy: 'cache-and-network',
    })
    const [createQuickReplyMutation, { loading: creatingQuickReply }] = useMutation(CREATE_MESSAGE_TEMPLATE)
    const [updateQuickReplyMutation, { loading: updatingQuickReply }] = useMutation(UPDATE_MESSAGE_TEMPLATE)
    const [toggleQuickReplyBlueprintMutation] = useMutation(TOGGLE_TEMPLATE_BLUEPRINT)

    // Auto-select config if only one available or match source
    useEffect(() => {
        if (availableConfigs.length > 0) {
            // If currently selected is not in available, reset
            if (selectedConfigId && !availableConfigs.find(c => c.id === selectedConfigId)) {
                setSelectedConfigId(undefined)
            }
        }
    }, [availableConfigs, selectedConfigId])
    const [filterWhatsApp, setFilterWhatsApp] = useState(true)
    const [filterSMS, setFilterSMS] = useState(true)
    const [showTagManager, setShowTagManager] = useState(false)
    const [showQREmoji, setShowQREmoji] = useState(false)
    const [showQRVars, setShowQRVars] = useState(false)
    const messagesEndRef = useRef<HTMLDivElement>(null)
    const quickReplyTextareaRef = useRef<HTMLTextAreaElement>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (!isAdmin && quickReplyScope === 'ORGANIZATION') {
            setQuickReplyScope('USER')
        }
    }, [isAdmin, quickReplyScope])

    const quickReplies = useMemo(() => {
        const templates = quickRepliesData?.messageTemplates ?? []
        return templates
            .map(toQuickReply)
            .filter((reply): reply is QuickReply => !!reply)
    }, [quickRepliesData])

    // Resolve the agent's numbers from the active config (must be before QR_VARIABLES)
    const agentWhatsAppNumber = useMemo(() => {
        const activeConfig = selectedConfigId
            ? availableConfigs.find((c) => c.id === selectedConfigId)
            : availableConfigs[0]
        return activeConfig?.whatsapp_number || null
    }, [availableConfigs, selectedConfigId])

    const agentSmsNumber = useMemo(() => {
        const activeConfig = selectedConfigId
            ? availableConfigs.find((c) => c.id === selectedConfigId)
            : availableConfigs[0]
        return activeConfig?.sms_number || null
    }, [availableConfigs, selectedConfigId])

    const QR_EMOJIS = [
        '😀', '😊', '😂', '🤣', '😍', '😘', '😎', '🤔', '😢', '😭',
        '😡', '🤯', '👍', '👎', '🙏', '💪', '👏', '🎉', '❤️', '🔥',
        '⭐', '✅', '❌', '⚠️', '💡', '📱', '📧', '💰', '🏠', '🚗',
        '🎯', '🔗', '📋', '🗓️', '⏰', '🔔', '💬', '📞', '✉️', '🤝',
    ]

    const QR_VARIABLES = [
        { key: '{firstname}', label: 'First name' },
        { key: '{lastname}', label: 'Last name' },
        { key: '{fullname}', label: 'Full name' },
        { key: '{phone}', label: 'Phone' },
        { key: '{agentname}', label: 'Agent name' },
        { key: '{organization}', label: 'Organization' },
        { key: '{MiNumeroWhatsApp}', label: `wa.me link${agentWhatsAppNumber ? ` (${agentWhatsAppNumber})` : ''}` },
    ]

    const insertAtCursorQR = (text: string) => {
        const el = quickReplyTextareaRef.current
        if (!el) {
            setQuickReplyContent((prev) => prev + text)
            return
        }
        const start = el.selectionStart ?? quickReplyContent.length
        const end = el.selectionEnd ?? quickReplyContent.length
        const newValue = quickReplyContent.slice(0, start) + text + quickReplyContent.slice(end)
        setQuickReplyContent(newValue)
        // Restore cursor after the inserted text
        requestAnimationFrame(() => {
            el.focus()
            el.setSelectionRange(start + text.length, start + text.length)
        })
    }

    const visibleQuickReplies = useMemo(() => {
        return quickReplies
            .filter((reply) => {
                if (reply.scope === 'USER') {
                    return !!currentUserId && reply.createdByUserId === currentUserId
                }

                return !!currentOrganizationId && reply.organizationId === currentOrganizationId
            })
            .sort((a, b) => {
                if (a.scope !== b.scope) return a.scope === 'USER' ? -1 : 1
                return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
            })
    }, [currentOrganizationId, currentUserId, quickReplies])

    // Calculate if WhatsApp session is active (last INBOUND message < 24 hours)
    const whatsappSessionInfo = useMemo(() => {
        const now = new Date()
        const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000)

        // Find the most recent INBOUND message
        const inboundMessages = messages.filter(m => m.direction === 'INBOUND' && m.channel === 'WHATSAPP')
        if (inboundMessages.length === 0) {
            return { isActive: false, lastInbound: null, hoursRemaining: 0 }
        }

        const lastInbound = inboundMessages.reduce((latest, msg) => {
            const msgDate = new Date(msg.created_at)
            const latestDate = latest ? new Date(latest.created_at) : new Date(0)
            return msgDate > latestDate ? msg : latest
        }, null as Message | null)

        if (!lastInbound) {
            return { isActive: false, lastInbound: null, hoursRemaining: 0 }
        }

        const lastInboundDate = new Date(lastInbound.created_at)
        const isActive = lastInboundDate > twentyFourHoursAgo
        const sessionExpiry = new Date(lastInboundDate.getTime() + 24 * 60 * 60 * 1000)
        const hoursRemaining = isActive ? Math.round((sessionExpiry.getTime() - now.getTime()) / (60 * 60 * 1000)) : 0

        return { isActive, lastInbound, hoursRemaining }
    }, [messages])

    // Auto-select channel based on session status
    useEffect(() => {
        if (whatsappSessionInfo.isActive) {
            setSelectedChannel('WHATSAPP')
        } else {
            setSelectedChannel('SMS')
        }
    }, [whatsappSessionInfo.isActive, activeConversation?.id])

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    useEffect(() => {
        scrollToBottom()
    }, [messages, activeConversation])

    useEffect(() => {
        const handlePaste = (e: ClipboardEvent) => {
            if (!e.clipboardData) return
            const files = Array.from(e.clipboardData.files || [])
            if (files.length === 0) return
            e.preventDefault()
            applyFile(files[0])
            setIsDragging(true)
            setTimeout(() => setIsDragging(false), 1000)
            toast.info('File pasted')
        }
        document.addEventListener('paste', handlePaste)
        return () => document.removeEventListener('paste', handlePaste)
    }, [])

    const applyFile = (file: File) => {
        setSelectedFile(file)
        if (file.type.startsWith('image/')) {
            const reader = new FileReader()
            reader.onload = (event) => {
                setFilePreviewUrl(event.target?.result as string)
            }
            reader.readAsDataURL(file)
        } else {
            setFilePreviewUrl(null)
        }
    }

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        applyFile(file)
    }

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault()
        setIsDragging(true)
    }

    const handleDragLeave = () => {
        setIsDragging(false)
    }

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault()
        setIsDragging(false)
        const file = e.dataTransfer.files?.[0]
        if (file) applyFile(file)
    }

    const handleRemoveFile = () => {
        setSelectedFile(null)
        setFilePreviewUrl(null)
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const handleSend = () => {
        if (!activeConversation) return
        if (!inputText.trim() && !selectedFile) return

        if (selectedFile && onSendMessageWithMedia) {
            onSendMessageWithMedia(activeConversation.id, inputText, selectedChannel, selectedFile, selectedConfigId)
            setSelectedFile(null)
            setFilePreviewUrl(null)
        } else {
            onSendMessage(activeConversation.id, inputText, selectedChannel, selectedConfigId)
        }
        setInputText('')
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSend()
        }
    }

    const handleOpenTemplate = () => {
        if (activeConversation && onOpenTemplateDrawer) {
            onOpenTemplateDrawer(activeConversation.id)
        }
    }

    const resetQuickReplyForm = () => {
        setQuickReplyTitle('')
        setQuickReplyContent('')
        setQuickReplyScope('USER')
        setEditingQuickReplyId(null)
    }

    const handleOpenQuickRepliesModal = () => {
        resetQuickReplyForm()
        setShowQuickRepliesModal(true)
    }

    const handleCloseQuickRepliesModal = () => {
        setShowQuickRepliesModal(false)
        resetQuickReplyForm()
    }

    const handleEditQuickReply = (reply: QuickReply) => {
        if (reply.scope === 'ORGANIZATION' && !isAdmin) {
            toast.error('Only administrators can edit organization quick replies')
            return
        }

        setQuickReplyTitle(reply.title)
        setQuickReplyContent(reply.content)
        setQuickReplyScope(reply.scope)
        setEditingQuickReplyId(reply.id)
    }

    const handleSaveQuickReply = async () => {
        const title = quickReplyTitle.trim()
        const content = quickReplyContent.trim()

        if (!title || !content) {
            toast.error('Add a title and message for the quick reply')
            return
        }

        if (!currentUserId) {
            toast.error('Unable to identify the current user')
            return
        }

        if (quickReplyScope === 'ORGANIZATION' && !isAdmin) {
            toast.error('Only administrators can create organization quick replies')
            return
        }

        if (quickReplyScope === 'ORGANIZATION' && !currentOrganizationId) {
            toast.error('Unable to identify the current organization')
            return
        }

        const metadata = {
            quick_reply: true,
            quick_reply_scope: quickReplyScope,
            quick_reply_owner_user_id: currentUserId,
            quick_reply_organization_id: quickReplyScope === 'ORGANIZATION' ? currentOrganizationId : null,
            enabled_channels: ['SMS', 'WHATSAPP'],
        }

        try {
            if (editingQuickReplyId) {
                const result = await updateQuickReplyMutation({
                    variables: {
                        id: editingQuickReplyId,
                        name: title,
                        body: content,
                        description: 'Quick reply',
                        metadata,
                        isActive: true,
                        enabledChannels: ['SMS', 'WHATSAPP'],
                    },
                })

                if (!result.data?.updateMessageTemplate?.ok) {
                    throw new Error(result.data?.updateMessageTemplate?.errors?.[0] || 'Unable to update quick reply')
                }

                await toggleQuickReplyBlueprintMutation({
                    variables: {
                        id: editingQuickReplyId,
                        isBlueprint: quickReplyScope === 'ORGANIZATION',
                    },
                })
            } else {
                const result = await createQuickReplyMutation({
                    variables: {
                        name: title,
                        body: content,
                        description: 'Quick reply',
                        channel: 'SMS',
                        metadata,
                        enabledChannels: ['SMS', 'WHATSAPP'],
                    },
                })

                if (!result.data?.createMessageTemplate?.ok) {
                    throw new Error(result.data?.createMessageTemplate?.errors?.[0] || 'Unable to create quick reply')
                }

                const createdTemplateId = result.data?.createMessageTemplate?.template?.id
                if (createdTemplateId) {
                    await toggleQuickReplyBlueprintMutation({
                        variables: {
                            id: createdTemplateId,
                            isBlueprint: quickReplyScope === 'ORGANIZATION',
                        },
                    })
                }
            }

            toast.success(editingQuickReplyId ? 'Quick reply updated' : 'Quick reply saved')
            await refetchQuickReplies()
            resetQuickReplyForm()
        } catch (error: any) {
            toast.error(error?.message || 'Unable to save quick reply')
        }
    }

    const handleDeleteQuickReply = async (replyId: string) => {
        try {
            const reply = quickReplies.find((item) => item.id === replyId)
            if (!reply) return

            const metadata = {
                quick_reply: true,
                quick_reply_scope: reply.scope,
                quick_reply_owner_user_id: reply.createdByUserId,
                quick_reply_organization_id: reply.organizationId,
                archived_from_chat: true,
            }

            const result = await updateQuickReplyMutation({
                variables: {
                    id: replyId,
                    metadata,
                    isActive: false,
                },
            })

            if (!result.data?.updateMessageTemplate?.ok) {
                throw new Error(result.data?.updateMessageTemplate?.errors?.[0] || 'Unable to archive quick reply')
            }

            if (editingQuickReplyId === replyId) {
                resetQuickReplyForm()
            }

            toast.success('Quick reply archived')
            await refetchQuickReplies()
        } catch (error: any) {
            toast.error(error?.message || 'Unable to archive quick reply')
        }
    }

    const handleUseQuickReply = (reply: QuickReply) => {
        const resolvedContent = personalizeQuickReply(
            reply.content,
            activeConversation,
            currentUserName,
            currentOrganizationName,
            agentWhatsAppNumber
        )

        setInputText((prev) => prev.trim() ? `${prev}\n${resolvedContent}` : resolvedContent)
        setShowQuickRepliesModal(false)
    }

    if (!activeConversation) {
        return (
            <div className='card card-flush h-100 d-flex justify-content-center align-items-center'>
                <div className='text-center'>
                    <i className='bi bi-chat-square-text fs-1 text-muted mb-3 d-block' style={{ fontSize: '4rem' }}></i>
                    <span className='text-muted fs-4'>Select a conversation to start chatting</span>
                </div>
            </div>
        )
    }

    return (
        <div className='card card-flush h-100 chat-window'>
            <div className='card-header' id='kt_chat_messenger_header'>
                <div className='card-title'>
                    <div className='d-flex justify-content-center flex-column me-3'>
                        <a href='#' className='fs-4 fw-bolder text-gray-900 text-hover-primary me-1 mb-2 lh-1'>
                            {activeConversation.full_name}
                        </a>
                        <div className='mb-2 lh-1 text-muted fs-7'>
                            <PhoneProviderActions
                                phone={activeConversation.phone}
                                stopPropagation={false}
                                textClassName='text-muted fs-7'
                            />
                        </div>
                        <div className='mb-0 lh-1 d-flex align-items-center'>
                            {whatsappSessionInfo.isActive ? (
                                <>
                                    <span className='badge badge-success badge-circle w-10px h-10px me-1'></span>
                                    <span className='fs-7 fw-bold text-success'>
                                        WhatsApp active ({whatsappSessionInfo.hoursRemaining}h remaining)
                                    </span>
                                </>
                            ) : (
                                <>
                                    <span className='badge badge-secondary badge-circle w-10px h-10px me-1'></span>
                                    <span className='fs-7 fw-bold text-muted'>
                                        WhatsApp session expired
                                    </span>
                                </>
                            )}

                        </div>
                        {configSource && (
                            <div className='fs-9 text-muted mt-1 d-flex align-items-center'>
                                <span className="me-1">Via:</span>
                                {configSource.startsWith('organization:') ? (
                                    <>
                                        <i className="bi bi-building text-muted fs-9 me-1"></i>
                                        {configSource.replace('organization:', '')}
                                    </>
                                ) : configSource.startsWith('user:') ? (
                                    <>
                                        <i className="bi bi-person text-muted fs-9 me-1"></i>
                                        {configSource.replace('user:', '')}
                                    </>
                                ) : (
                                    configSource
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className='card-toolbar d-flex align-items-center gap-3'>
                    <div className='d-flex align-items-center gap-3'>
                        <label className='form-check form-check-sm form-check-custom form-check-solid d-flex align-items-center gap-1 cursor-pointer mb-0'>
                            <input
                                className='form-check-input'
                                type='checkbox'
                                checked={filterWhatsApp}
                                onChange={(e) => setFilterWhatsApp(e.target.checked)}
                                style={{ borderColor: '#25D366' }}
                            />
                            <span className='form-check-label fs-7'>
                                <i className='bi bi-whatsapp text-success me-1'></i>WhatsApp
                            </span>
                        </label>
                        <label className='form-check form-check-sm form-check-custom form-check-solid d-flex align-items-center gap-1 cursor-pointer mb-0'>
                            <input
                                className='form-check-input'
                                type='checkbox'
                                checked={filterSMS}
                                onChange={(e) => setFilterSMS(e.target.checked)}
                            />
                            <span className='form-check-label fs-7'>
                                <i className='bi bi-chat-dots text-muted me-1'></i>SMS
                            </span>
                        </label>
                    </div>
                    {!whatsappSessionInfo.isActive && onOpenTemplateDrawer && (
                        <button
                            className='btn btn-sm btn-light-success'
                            onClick={handleOpenTemplate}
                            title='Send WhatsApp Template to reopen conversation'
                        >
                            <i className='bi bi-whatsapp me-1'></i>
                            Send Template
                        </button>
                    )}

                    <button
                        className='btn btn-sm btn-light btn-active-light-primary ms-2'
                        onClick={() => setShowTagManager(true)}
                        title="Tags Manager"
                    >
                        <i className='bi bi-tags fs-2'></i>
                    </button>
                </div>
            </div>

            <div className='card-body d-flex flex-column' id='kt_chat_messenger_body'>
                <div
                    className='scroll-y me-n5 pe-5 flex-grow-1'
                    data-kt-element='messages'
                    data-kt-scroll='true'
                    data-kt-scroll-activate='{default: false, lg: true}'
                    data-kt-scroll-max-height='auto'
                    data-kt-scroll-dependencies='#kt_header, #kt_app_header, #kt_toolbar, #kt_app_toolbar, #kt_footer, #kt_app_footer, #kt_chat_messenger_header, #kt_chat_messenger_footer'
                    data-kt-scroll-wrappers='#kt_content, #kt_app_content, #kt_chat_messenger_body'
                    data-kt-scroll-offset='5px'
                    style={{ overflowY: 'auto' }}
                >
                    {loading && (
                        <div className="d-flex justify-content-center my-4">
                            <span className="spinner-border text-primary" role="status"></span>
                        </div>
                    )}

                    {(() => {
                        let lastDateStr = ''
                        return messages.filter(msg => {
                            if (msg.channel === 'WHATSAPP' && !filterWhatsApp) return false
                            if (msg.channel === 'SMS' && !filterSMS) return false
                            return true
                        }).flatMap((msg) => {
                            const msgDate = new Date(msg.created_at)
                            const dateStr = `${msgDate.getFullYear()}-${msgDate.getMonth()}-${msgDate.getDate()}`
                            const elements: React.ReactNode[] = []

                            if (dateStr !== lastDateStr) {
                                lastDateStr = dateStr
                                elements.push(
                                    <div key={`sep-${dateStr}`} className='d-flex align-items-center my-5'>
                                        <div className='flex-grow-1' style={{ borderTop: '1px solid #e4e6ef' }}></div>
                                        <span className='mx-3 px-3 py-1 rounded-pill bg-light text-muted fs-8 fw-bold'>
                                            {formatDateSeparator(msgDate)}
                                        </span>
                                        <div className='flex-grow-1' style={{ borderTop: '1px solid #e4e6ef' }}></div>
                                    </div>
                                )
                            }

                            elements.push(
                        <div
                            key={msg.id || Math.random()}
                            className={`d-flex justify-content-${msg.direction === 'OUTBOUND' ? 'end' : 'start'} mb-8`}
                        >
                            <div
                                className={`d-flex flex-column align-items-${msg.direction === 'OUTBOUND' ? 'end' : 'start'}`}
                            >
                                <div className='d-flex align-items-center mb-2 gap-2'>
                                    {msg.direction === 'INBOUND' && (
                                        <div className='symbol symbol-30px symbol-circle'>
                                            <span className='symbol-label bg-light-danger text-danger fs-7 fw-bolder'>
                                                {activeConversation.full_name.charAt(0)}
                                            </span>
                                        </div>
                                    )}
                                    <span className='text-muted fs-8'>
                                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </div>

                                <div
                                    className={`p-5 rounded fw-bold mw-lg-400px text-start`}
                                    data-kt-element='message-text'
                                    style={{
                                        whiteSpace: 'pre-wrap',
                                        ...(msg.channel === 'WHATSAPP'
                                            ? msg.direction === 'OUTBOUND'
                                                ? { backgroundColor: '#dcf8c6', color: '#1a1a1a' }
                                                : { backgroundColor: '#ffffff', color: '#1a1a1a', border: '1px solid #e8e8e8' }
                                            : msg.direction === 'OUTBOUND'
                                                ? { backgroundColor: '#3e97ff', color: '#ffffff' }
                                                : { backgroundColor: '#f1f3f5', color: '#1a1a1a' }
                                        )
                                    }}
                                >
                                    {msg.content && !msg.metadata?.media_urls?.length && msg.content}
                                    {msg.metadata?.media_urls?.length > 0 && (
                                        <div className='d-flex flex-column gap-2'>
                                            {msg.metadata.media_urls.map((mediaUrl: string, idx: number) => {
                                                const contentType = msg.metadata?.[`MediaContentType${idx}`]?.[0] || ''
                                                const isImage = contentType.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(mediaUrl)
                                                const token = localStorage.getItem(STORAGE_NAMES.TOKEN) || ''
                                                const proxyUrl = `${BACKEND_URL}/api/chat/media-proxy?url=${encodeURIComponent(mediaUrl)}&token=${token}`
                                                return (
                                                    <div key={idx}>
                                                        {isImage ? (
                                                            <a href={proxyUrl} target='_blank' rel='noopener noreferrer'>
                                                                <img
                                                                    src={proxyUrl}
                                                                    alt='Attachment'
                                                                    style={{ maxWidth: '260px', maxHeight: '260px', borderRadius: '8px', cursor: 'pointer' }}
                                                                />
                                                            </a>
                                                        ) : (
                                                            <a href={proxyUrl} target='_blank' rel='noopener noreferrer' className='d-flex align-items-center gap-2 text-decoration-none'>
                                                                <i className='bi bi-file-earmark-arrow-down fs-4'></i>
                                                                <span>Attachment {idx + 1}</span>
                                                            </a>
                                                        )}
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    )}
                                </div>

                                <div className={`d-flex align-items-center mt-1 ${msg.direction === 'OUTBOUND' ? 'justify-content-end' : 'justify-content-start'}`}>
                                    {(() => {
                                        const badgeInfo = getChannelBadgeInfo(msg)
                                        if (badgeInfo) {
                                            return (
                                                <span
                                                    className="badge badge-light fs-8 d-flex align-items-center"
                                                    title={getChannelBadgeTooltip(msg)}
                                                    style={{ cursor: 'help', backgroundColor: badgeInfo.backgroundColor, color: badgeInfo.color }}
                                                >
                                                    <i className={`bi ${badgeInfo.icon} me-1`} style={{ fontSize: '0.75rem' }}></i>
                                                    {badgeInfo.label} {badgeInfo.shortPhone}
                                                </span>
                                            )
                                        }
                                        return (
                                            <>
                                                {msg.channel === 'WHATSAPP' ? (
                                                    <span className="badge badge-light-success fs-8 d-flex align-items-center">
                                                        <i className="bi bi-whatsapp text-success me-1" style={{ fontSize: '0.75rem' }}></i>
                                                        WhatsApp
                                                    </span>
                                                ) : (
                                                    <span className="badge badge-light fs-8 d-flex align-items-center">
                                                        <i className="bi bi-chat-dots text-muted me-1" style={{ fontSize: '0.75rem' }}></i>
                                                        SMS
                                                    </span>
                                                )}
                                            </>
                                        )
                                    })()}
                                    {msg.direction === 'OUTBOUND' && (
                                        <span className="ms-2">
                                            {msg.status === 'READ' ? <i className="bi bi-check2-all text-primary"></i> : <i className="bi bi-check2 text-muted"></i>}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                            )
                            return elements
                        })
                    })()}

                    {messages.length > 0 && !messages.some(msg =>
                        (msg.channel === 'WHATSAPP' && filterWhatsApp) || (msg.channel === 'SMS' && filterSMS)
                    ) && (
                            <div className='text-center text-muted py-5'>
                                <i className='bi bi-funnel fs-1 d-block mb-2'></i>
                                <span>No messages matching selected filters</span>
                            </div>
                        )}
                    <div ref={messagesEndRef} />
                </div>
            </div>

            <div
                className='card-footer p-3 position-relative d-flex flex-column'
                id='kt_chat_messenger_footer'
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
                    outline: isDragging ? '2px dashed #009ef7' : undefined,
                    borderRadius: isDragging ? '8px' : undefined,
                    transition: 'outline 0.2s, border-radius 0.2s'
                }}
            >
                {isDragging && (
                    <div className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-light-primary' style={{ zIndex: 10, pointerEvents: 'none', opacity: 0.85, borderRadius: 'inherit' }}>
                        <div className='text-primary fw-bold fs-5'>
                            <i className='bi bi-cloud-upload fs-1 d-block text-center mb-2'></i>
                            Drop file here
                        </div>
                    </div>
                )}

                {!whatsappSessionInfo.isActive && (
                    <div className='alert alert-warning d-flex align-items-center py-2 px-3 mb-3'>
                        <i className='bi bi-exclamation-triangle-fill text-warning me-2'></i>
                        <small>WhatsApp session expired. Use SMS or send a Template to reopen.</small>
                    </div>
                )}

                <textarea
                    className='form-control mb-2'
                    rows={3}
                    data-kt-element='input'
                    placeholder='Type a message'
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    style={{ borderColor: '#e2e8f0', resize: 'none' }}
                ></textarea>

                {selectedFile && (
                    <div className='mb-2 p-2 border rounded bg-light-info d-flex align-items-center justify-content-between gap-2' style={{ minHeight: '45px' }}>
                        <div className='d-flex align-items-center gap-2 flex-grow-1 min-w-0'>
                            {filePreviewUrl ? (
                                <img
                                    src={filePreviewUrl}
                                    alt='Preview'
                                    style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '3px', flexShrink: 0 }}
                                />
                            ) : (
                                <i className='bi bi-file-earmark fs-6 text-muted' style={{ flexShrink: 0 }}></i>
                            )}
                            <div className='min-w-0'>
                                <div className='text-truncate fw-bold fs-9'>{selectedFile.name}</div>
                                <small className='text-muted d-block' style={{ fontSize: '0.7rem' }}>{(selectedFile.size / 1024).toFixed(0)} KB</small>
                            </div>
                        </div>
                        <button
                            type='button'
                            className='btn btn-xs btn-icon btn-light-danger'
                            onClick={handleRemoveFile}
                            title='Remove file'
                            style={{ flexShrink: 0 }}
                        >
                            <i className='bi bi-x fs-7'></i>
                        </button>
                    </div>
                )}

                {/* Action Buttons & Controls Row */}
                <div className='d-flex gap-2 align-items-center flex-wrap'>
                    <input
                        ref={fileInputRef}
                        type='file'
                        hidden
                        accept='image/*,video/mp4,audio/mpeg,application/pdf'
                        onChange={handleFileSelect}
                    />

                    <button
                        className='btn btn-sm btn-icon btn-active-light-primary'
                        type='button'
                        onClick={() => fileInputRef.current?.click()}
                        title='Attach file'
                    >
                        <i className='bi bi-paperclip fs-3'></i>
                    </button>

                    <button
                        className='btn btn-sm btn-icon btn-light'
                        onClick={() => setShowTagManager(true)}
                        title="Manage Tags"
                    >
                        <i className='bi bi-tags'></i>
                    </button>

                    <button
                        className='btn btn-sm btn-light-primary'
                        onClick={handleOpenQuickRepliesModal}
                        title='Quick Replies'
                    >
                        <i className='bi bi-lightning-charge me-1'></i>
                        Quick Replies
                    </button>

                    {onOpenTemplateDrawer && (
                        <button className='btn btn-sm btn-icon btn-light' onClick={handleOpenTemplate} title="Templates">
                            <i className='bi bi-file-text'></i>
                        </button>
                    )}

                    {/* Channel Config Selector */}
                    {availableConfigs.length > 0 && (
                        <div style={{ flex: '0 1 110px' }}>
                            <select
                                className="form-select form-select-sm"
                                value={selectedConfigId || ''}
                                onChange={(e) => setSelectedConfigId(e.target.value ? Number(e.target.value) : undefined)}
                                title="Send using..."
                            >
                                <option value="">Default Channel</option>
                                {availableConfigs.map(config => (
                                    <option key={config.id} value={config.id}>
                                        {config.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Channel Selector */}
                    <div className='d-flex flex-column' style={{ flex: '0 1 110px' }}>
                        <select
                            className='form-select form-select-sm'
                            value={selectedChannel}
                            onChange={(e) => setSelectedChannel(e.target.value as 'WHATSAPP' | 'SMS')}
                        >
                            <option
                                value='WHATSAPP'
                                disabled={!whatsappSessionInfo.isActive}
                            >
                                📱 WhatsApp {!whatsappSessionInfo.isActive && '(expired)'}
                            </option>
                            <option value='SMS'>
                                💬 SMS
                            </option>
                        </select>
                        {selectedChannel === 'WHATSAPP' && agentWhatsAppNumber && (
                            <span className='text-muted fs-9 mt-1' style={{ fontSize: '0.7rem' }}>
                                <i className='bi bi-whatsapp text-success me-1'></i>{agentWhatsAppNumber}
                            </span>
                        )}
                        {selectedChannel === 'SMS' && agentSmsNumber && (
                            <span className='text-muted fs-9 mt-1' style={{ fontSize: '0.7rem' }}>
                                <i className='bi bi-chat-dots me-1'></i>{agentSmsNumber}
                            </span>
                        )}
                    </div>

                    {/* Send Button */}
                    <button
                        className={`btn btn-sm ${selectedChannel === 'WHATSAPP' ? 'btn-success' : 'btn-secondary'}`}
                        type='button'
                        data-kt-element='send'
                        onClick={handleSend}
                        disabled={!inputText.trim() && !selectedFile}
                        style={{ flex: '0 0 auto' }}
                    >
                        Send
                    </button>
                </div>
            </div>
            {/* Template Drawer or other modals could be here */}
            {activeConversation && (
                <TagManager
                    show={showTagManager}
                    onHide={() => setShowTagManager(false)}
                    contactId={activeConversation.id}
                    currentTags={activeConversation.tags || []}
                    availableTags={availableTags}
                    onCreateTag={onCreateTag}
                    onManageTag={onManageTag}
                />
            )}
            <Modal show={showQuickRepliesModal} onHide={handleCloseQuickRepliesModal} size='xl' centered>
                <Modal.Header closeButton>
                    <Modal.Title>Quick Replies</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className='row g-5'>
                        <div className='col-lg-7'>
                            <div className='d-flex align-items-center justify-content-between mb-4'>
                                <div>
                                    <h5 className='mb-1'>Saved replies</h5>
                                    <div className='text-muted fs-7'>
                                        Use placeholders like <code>{'{firstname}'}</code>, <code>{'{lastname}'}</code>, <code>{'{fullname}'}</code>, <code>{'{phone}'}</code>, <code>{'{agentname}'}</code>.
                                    </div>
                                </div>
                            </div>

                            <div className='d-flex flex-column gap-3' style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                                {quickRepliesLoading && (
                                    <div className='border rounded p-4 text-muted bg-light'>
                                        Loading quick replies...
                                    </div>
                                )}

                                {visibleQuickReplies.length === 0 && (
                                    <div className='border rounded p-4 text-muted bg-light'>
                                        {quickRepliesLoading ? 'Syncing data...' : 'No quick replies yet. Create your first one on the right.'}
                                    </div>
                                )}

                                {visibleQuickReplies.map((reply) => (
                                    <div key={reply.id} className='border rounded p-4'>
                                        <div className='d-flex justify-content-between align-items-start gap-3 mb-2'>
                                            <div>
                                                <div className='fw-bold text-gray-900'>{reply.title}</div>
                                                <span className={`badge ${reply.scope === 'USER' ? 'badge-light-primary' : 'badge-light-success'} mt-1`}>
                                                    {reply.scope === 'USER' ? 'My reply' : 'Organization'}
                                                </span>
                                            </div>
                                            <div className='d-flex gap-2'>
                                                <Button variant='light-primary' size='sm' onClick={() => handleUseQuickReply(reply)}>
                                                    Use
                                                </Button>
                                                {(reply.scope === 'USER' || isAdmin) && (
                                                    <Button variant='light' size='sm' onClick={() => handleEditQuickReply(reply)}>
                                                        Edit
                                                    </Button>
                                                )}
                                                {(reply.scope === 'USER' || isAdmin) && (
                                                    <Button variant='light-danger' size='sm' onClick={() => handleDeleteQuickReply(reply.id)}>
                                                        Archive
                                                    </Button>
                                                )}
                                            </div>
                                        </div>

                                        <div className='text-muted fs-7 mb-2'>
                                            Preview
                                        </div>
                                        <div className='bg-light rounded p-3 text-gray-800' style={{ whiteSpace: 'pre-wrap' }}>
                                            {personalizeQuickReply(reply.content, activeConversation, currentUserName, currentOrganizationName, agentWhatsAppNumber)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className='col-lg-5'>
                            <div className='border rounded p-4 bg-light'>
                                <h5 className='mb-4'>{editingQuickReplyId ? 'Edit quick reply' : 'New quick reply'}</h5>

                                <Form.Group className='mb-3'>
                                    <Form.Label>Title</Form.Label>
                                    <Form.Control
                                        value={quickReplyTitle}
                                        onChange={(e) => setQuickReplyTitle(e.target.value)}
                                        placeholder='Example: Welcome with link'
                                    />
                                </Form.Group>

                                <Form.Group className='mb-3'>
                                    <Form.Label>Scope</Form.Label>
                                    <Form.Select
                                        value={quickReplyScope}
                                        onChange={(e) => setQuickReplyScope(e.target.value as QuickReplyScope)}
                                    >
                                        <option value='USER'>Only for me</option>
                                        <option value='ORGANIZATION' disabled={!isAdmin}>
                                            Organization {!isAdmin ? '(admins only)' : ''}
                                        </option>
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group className='mb-3'>
                                    <Form.Label>Message</Form.Label>

                                    {/* Toolbar */}
                                    <div className='d-flex align-items-center gap-1 px-2 py-1 border border-bottom-0 rounded-top bg-light'>
                                        {/* Emoji picker */}
                                        <div className='position-relative'>
                                            <button
                                                type='button'
                                                className='btn btn-sm btn-icon btn-light-warning'
                                                title='Emojis'
                                                onClick={() => { setShowQREmoji(!showQREmoji); setShowQRVars(false) }}
                                            >
                                                😀
                                            </button>
                                            {showQREmoji && (
                                                <div className='position-absolute top-100 start-0 z-index-3 bg-white border rounded shadow p-2' style={{ width: 260, zIndex: 1050 }}>
                                                    <div className='d-flex justify-content-between align-items-center mb-2'>
                                                        <small className='text-muted'>Common emojis</small>
                                                        <button type='button' className='btn btn-xs btn-icon btn-light' onClick={() => setShowQREmoji(false)}>
                                                            <i className='bi bi-x'></i>
                                                        </button>
                                                    </div>
                                                    <div className='d-flex flex-wrap gap-1'>
                                                        {QR_EMOJIS.map((emoji) => (
                                                            <button
                                                                key={emoji}
                                                                type='button'
                                                                className='btn btn-xs btn-light p-1 fs-5'
                                                                style={{ lineHeight: 1 }}
                                                                onClick={() => { insertAtCursorQR(emoji); setShowQREmoji(false) }}
                                                            >
                                                                {emoji}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Variable picker */}
                                        <div className='position-relative'>
                                            <button
                                                type='button'
                                                className='btn btn-sm btn-light-primary'
                                                title='Insert variable'
                                                onClick={() => { setShowQRVars(!showQRVars); setShowQREmoji(false) }}
                                            >
                                                <i className='bi bi-braces me-1'></i>Variables
                                            </button>
                                            {showQRVars && (
                                                <div className='position-absolute top-100 start-0 z-index-3 bg-white border rounded shadow' style={{ minWidth: 200, zIndex: 1050 }}>
                                                    <div className='d-flex justify-content-between align-items-center px-3 pt-2 pb-1'>
                                                        <small className='text-muted'>Insert variable</small>
                                                        <button type='button' className='btn btn-xs btn-icon btn-light' onClick={() => setShowQRVars(false)}>
                                                            <i className='bi bi-x'></i>
                                                        </button>
                                                    </div>
                                                    {QR_VARIABLES.map((v) => (
                                                        <button
                                                            key={v.key}
                                                            type='button'
                                                            className='d-flex align-items-center gap-2 w-100 text-start btn btn-light border-0 rounded-0 px-3 py-2'
                                                            onClick={() => { insertAtCursorQR(v.key); setShowQRVars(false) }}
                                                        >
                                                            <i className='bi bi-braces text-primary'></i>
                                                            <code className='text-primary'>{v.key}</code>
                                                            <span className='text-muted small ms-1'>{v.label}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <Form.Control
                                        as='textarea'
                                        rows={7}
                                        ref={quickReplyTextareaRef as any}
                                        value={quickReplyContent}
                                        onChange={(e) => setQuickReplyContent(e.target.value)}
                                        placeholder={'Hola {firstname}, para una comunicacion mas fluida puedes escribir a este enlace https://bit.ly/sendme'}
                                        className='rounded-top-0'
                                        style={{ borderTop: 'none' }}
                                    />
                                </Form.Group>

                                <div className='text-muted fs-7 mb-4'>
                                    The message is personalized when used in chat. Placeholders: <code className='text-primary'>{'{firstname}'}</code>, <code className='text-primary'>{'{agentname}'}</code>, etc.
                                </div>

                                <div className='d-flex justify-content-end gap-2'>
                                    {editingQuickReplyId && (
                                        <Button variant='light' onClick={resetQuickReplyForm}>
                                            Cancel edit
                                        </Button>
                                    )}
                                    <Button variant='primary' onClick={handleSaveQuickReply} disabled={creatingQuickReply || updatingQuickReply}>
                                        {editingQuickReplyId ? 'Update' : 'Save'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </div >
    )
}

export { ChatWindow }
